import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { getAdminId } from '../utils/getAdminId';
import type { SenderAddress } from '../types/shipping';

export const useSenderAddress = () => {
  const { user } = useAuth();
  const [senderAddress, setSenderAddress] = useState<SenderAddress | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const adminId = getAdminId(user);

  const loadSenderAddress = useCallback(async () => {
    if (!adminId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('users')
        .select(`
          shipping_street,
          shipping_house_number,
          shipping_house_number_ext,
          shipping_postal_code,
          shipping_city,
          shipping_country_code,
          shipping_contact_name,
          company_name,
          shipping_insurance_per_item,
          shipping_insurance_sdr_rate,
          shipping_shipment_type,
          shipping_default_package_type,
          shipping_globalpack_insurance,
          shipping_value_split,
          ai_address_enabled,
          postnl_product_codes
        `)
        .eq('id', adminId)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (data) {
        const isConfigured = Boolean(
          data.shipping_street &&
          data.shipping_house_number &&
          data.shipping_postal_code &&
          data.shipping_city
        );

        const insurancePerItem = data.shipping_insurance_per_item != null
          ? Number(data.shipping_insurance_per_item)
          : 5;
        const rawSdrRate = data.shipping_insurance_sdr_rate != null
          ? Number(data.shipping_insurance_sdr_rate)
          : 1;
        const insuranceSdrRate = Number.isFinite(rawSdrRate) && rawSdrRate > 0 ? rawSdrRate : 1;
        const validShipmentTypes = ['Gift', 'Documents', 'Commercial Goods', 'Commercial Sample', 'Returned Goods'] as const;
        const shipmentType = data.shipping_shipment_type && validShipmentTypes.includes(data.shipping_shipment_type as typeof validShipmentTypes[number])
          ? (data.shipping_shipment_type as typeof validShipmentTypes[number])
          : 'Commercial Goods';
        const defaultPackageType = data.shipping_default_package_type === 'mailbox' ? 'mailbox' : 'package';
        const rawValueSplit = data.shipping_value_split != null ? Number(data.shipping_value_split) : 1;
        const valueSplit = Number.isFinite(rawValueSplit) && rawValueSplit >= 1 && rawValueSplit <= 5
          ? (rawValueSplit as 1 | 2 | 3 | 4 | 5)
          : 1;
        const globalpackInsurance = data.shipping_globalpack_insurance === true;
        const aiAddressEnabled = data.ai_address_enabled === true;
        const postnlProductCodes = data.postnl_product_codes || {};
        setSenderAddress({
          street: data.shipping_street || '',
          houseNumber: data.shipping_house_number || '',
          houseNumberExt: data.shipping_house_number_ext || '',
          postalCode: data.shipping_postal_code || '',
          city: data.shipping_city || '',
          countryCode: data.shipping_country_code || 'NL',
          contactName: data.shipping_contact_name || data.company_name || '',
          insurancePerItem: Number.isFinite(insurancePerItem) ? Math.max(0, Math.min(10000, insurancePerItem)) : 5,
          insuranceSdrRate,
          shipmentType,
          defaultPackageType,
          globalpackInsurance,
          valueSplit,
          openaiApiKey: '',
          aiAddressEnabled,
          postnlProductCodes,
          isConfigured,
        });
      }
    } catch (err) {
      setError('Failed to load sender address');
      console.error('Error loading sender address:', err);
    } finally {
      setLoading(false);
    }
  }, [adminId]);

  useEffect(() => {
    loadSenderAddress();
  }, [loadSenderAddress]);

  const saveSenderAddress = async (address: Omit<SenderAddress, 'isConfigured'>): Promise<boolean> => {
    if (!adminId) return false;

    setSaving(true);
    setError(null);

    const insurancePerItem = address.insurancePerItem != null
      ? Math.max(0, Math.min(10000, Number(address.insurancePerItem)))
      : 5;
    const rawSdrRate = address.insuranceSdrRate != null ? Number(address.insuranceSdrRate) : 1;
    const insuranceSdrRate = Number.isFinite(rawSdrRate) && rawSdrRate > 0 ? rawSdrRate : 1;

    try {
      const validShipmentTypes = ['Gift', 'Documents', 'Commercial Goods', 'Commercial Sample', 'Returned Goods'] as const;
      const shipmentType = address.shipmentType && validShipmentTypes.includes(address.shipmentType)
        ? address.shipmentType
        : 'Commercial Goods';

      const defaultPackageType = address.defaultPackageType === 'mailbox' ? 'mailbox' : 'package';
      const valueSplit = address.valueSplit != null && address.valueSplit >= 1 && address.valueSplit <= 5
        ? address.valueSplit
        : 1;
      const globalpackInsurance = address.globalpackInsurance === true;

      const { error: updateError } = await supabase
        .from('users')
        .update({
          shipping_street: address.street,
          shipping_house_number: address.houseNumber,
          shipping_house_number_ext: address.houseNumberExt || null,
          shipping_postal_code: address.postalCode,
          shipping_city: address.city,
          shipping_country_code: address.countryCode,
          shipping_contact_name: address.contactName || null,
          shipping_insurance_per_item: insurancePerItem,
          shipping_insurance_sdr_rate: insuranceSdrRate,
          shipping_shipment_type: shipmentType,
          shipping_default_package_type: defaultPackageType,
          shipping_globalpack_insurance: globalpackInsurance,
          shipping_value_split: valueSplit,
          ...(address.openaiApiKey ? { openai_api_key: address.openaiApiKey } : {}),
          ai_address_enabled: address.aiAddressEnabled === true,
          postnl_product_codes: address.postnlProductCodes || {},
        })
        .eq('id', adminId);

      if (updateError) throw updateError;

      setSenderAddress({
        ...address,
        insurancePerItem,
        insuranceSdrRate,
        shipmentType,
        defaultPackageType,
        globalpackInsurance,
        valueSplit,
        openaiApiKey: address.openaiApiKey || '',
        aiAddressEnabled: address.aiAddressEnabled === true,
        postnlProductCodes: address.postnlProductCodes || {},
        isConfigured: Boolean(
          address.street &&
          address.houseNumber &&
          address.postalCode &&
          address.city
        ),
      });

      return true;
    } catch (err) {
      setError('Failed to save sender address');
      console.error('Error saving sender address:', err);
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    senderAddress,
    loading,
    saving,
    error,
    saveSenderAddress,
    refreshSenderAddress: loadSenderAddress,
    isConfigured: senderAddress?.isConfigured || false,
  };
};
